import { ComboButton, MenuItem } from "@carbon/react";
import { useModal } from "@flaminem/ui-components";
import React, { useState } from "react";

import { AuthorizedItem } from "src/authentication/AuthorizedMenu.tsx";
import { Scope, ScopeResource } from "src/authentication/PermissionsProvider.tsx";
import MenuButton from "src/components/MenuButton/MenuButton.tsx";
import { ModalTransition } from "src/components/Modal";
import { TabPanelHeader } from "src/components/TabPanel/TabPanelHeader.tsx";
import { ExecuteScenarioModal } from "src/features/scenario/ExecuteScenario";
import { useMediaQuery, BREAKPOINT_LARGE } from "src/hooks/useMediaQuery.ts";
import { useT } from "src/internationalization";
import { ExecuteRuleModal } from "src/pages/Cases/execute-rule/ExecuteRuleModal";
import { CaseSummary } from "src/types/case";

import AlertsAndExecutionsTab, { AlertsAndExecutionsTabProps } from "./AlertsAndExecutionsTab";

export type AlertsAndExecutionsResultsViewProps = Omit<
  AlertsAndExecutionsTabProps,
  "setIsDisplayExecutionButton"
> & {
  snapshotMode?: boolean;
  selectedCase?: { typeCode: string };
};

const AlertsAndExecutionsResultsView: React.FC<AlertsAndExecutionsResultsViewProps> = ({
  executionResults,
  selectedCase,
  snapshotMode,
  alerts,
  selectedAlert,
}) => {
  const [isDisplayExecutionButton, setIsDisplayExecutionButton] = useState<boolean>(false);
  const {
    openModal: openExecuteScenarioModal,
    isModalOpen: isExecuteScenarioModalOpen,
    closeModal: closeExecuteScenarioModal,
  } = useModal();
  const {
    openModal: openExecuteRuleModal,
    isModalOpen: isExecuteRuleModalOpen,
    closeModal: closeExecuteRuleModal,
  } = useModal();
  const matches = useMediaQuery(BREAKPOINT_LARGE);

  const t = useT();

  const renderExecuteScenarioMenuItem = () => (
    <AuthorizedItem
      requiredPermissions={[{ resource: ScopeResource.SCENARIO, scopes: [Scope.EXECUTE] }]}
    >
      <MenuItem
        label={t("page.case.tab.executions.button.run")}
        onClick={openExecuteScenarioModal}
      />
    </AuthorizedItem>
  );

  const renderTestRuleMenuItem = () => (
    <AuthorizedItem
      requiredPermissions={[{ resource: ScopeResource.RULE, scopes: [Scope.EXECUTE] }]}
    >
      <MenuItem
        label={t("page.case.tab.executions.button.testRule")}
        onClick={openExecuteRuleModal}
      />
    </AuthorizedItem>
  );

  const renderActionButtons = () => {
    if (!selectedCase || snapshotMode) return null;

    if (!matches) {
      return (
        <MenuButton label={t("general.actions.all")} menuAlignment="bottom-end">
          {renderExecuteScenarioMenuItem()}
          {renderTestRuleMenuItem()}
        </MenuButton>
      );
    }

    return (
      <>
        <AuthorizedItem
          requiredPermissions={[{ resource: ScopeResource.SCENARIO, scopes: [Scope.EXECUTE] }]}
        >
          <ComboButton
            label={t("page.case.tab.executions.button.run")}
            onClick={openExecuteScenarioModal}
            tooltipAlignment="top-right"
          >
            {renderTestRuleMenuItem()}
          </ComboButton>
        </AuthorizedItem>
      </>
    );
  };

  return (
    <>
      <div className="flex flex-col !p-0" style={{ minHeight: "calc(100vh - 15rem)" }}>
        <div className="flex items-center justify-between pr-4">
          <TabPanelHeader
            title={t("page.case.tab.alerts.and.executions.title")}
            description={t("page.case.tab.alerts.and.executions.description")}
          />
          {isDisplayExecutionButton && <div className="flex">{renderActionButtons()}</div>}
        </div>
        <div className="full-height">
          <AlertsAndExecutionsTab
            setIsDisplayExecutionButton={setIsDisplayExecutionButton}
            executionResults={executionResults}
            selectedCase={
              selectedCase ? { id: selectedCase.id, request: selectedCase.request } : undefined
            }
            alerts={alerts}
            selectedAlert={selectedAlert}
          />
        </div>
      </div>
      <ModalTransition>
        {selectedCase && isExecuteScenarioModalOpen && (
          <ExecuteScenarioModal
            showLink={false}
            open={isExecuteScenarioModalOpen ?? false}
            selectedCase={selectedCase}
            onClose={closeExecuteScenarioModal}
          />
        )}
        {selectedCase && isExecuteRuleModalOpen && (
          <ExecuteRuleModal
            caseTypeCode={selectedCase.typeCode}
            selectedCase={selectedCase as unknown as CaseSummary}
            onClose={closeExecuteRuleModal}
          />
        )}
      </ModalTransition>
    </>
  );
};

export default AlertsAndExecutionsResultsView;
